import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Button, ButtonGroup } from "reactstrap";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getRecords } from "../../services/api.service";

import StatCard from "./StatCard";
import AlertQtyChart from "./AlertQtyChart";
import JobFlowChart from "./JobFlowChart";
import PendingJobs from "./PendingJobs";

import "./dashboard.scss";

/**
 * DASHBOARD
 * ---------------------------------------------------------------------------
 * The repair flow this screen reports on:
 *
 *   Item Intake (Incoming) -> Job Card (Pending / Under Repair)
 *        -> Repair Completed -> Dispatch (Outgoing) -> Outgoing Invoice
 *
 * Incoming and Outgoing follow the selected period. Pending and Alert Qty are
 * live figures - they describe where things stand right now - so the period
 * buttons do not change them, and each card says which it is.
 *
 * Everything is served by a single call to /dashboard/summary.
 * ---------------------------------------------------------------------------
 */

const EMPTY = {
  cards: { incoming: 0, outgoing: 0, pending: 0, alerts: 0 },
  pendingBreakdown: [],
  trend: [],
  alertItems: [],
  alertSource: "alert_qty",
};

/* yyyy-mm-dd in the user's own timezone (toISOString would shift the day). */
const toInputDate = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`;
};

const buildRange = (key) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (key) {
    case "today":
      return { from: toInputDate(today), to: toInputDate(today) };

    case "week": {
      /* week starts on Monday */
      const day = (today.getDay() + 6) % 7;
      const monday = new Date(today);
      monday.setDate(today.getDate() - day);
      return { from: toInputDate(monday), to: toInputDate(today) };
    }

    case "year":
      return {
        from: toInputDate(new Date(today.getFullYear(), 0, 1)),
        to: toInputDate(today),
      };

    case "month":
    default:
      return {
        from: toInputDate(new Date(today.getFullYear(), today.getMonth(), 1)),
        to: toInputDate(today),
      };
  }
};

const BREADCRUMBS = [{ title: "Dashboard", link: "#" }];

const RANGES = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "year", label: "This Year" },
];

const Dashboard = () => {
  const [rangeKey, setRangeKey] = useState("month");
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const range = useMemo(() => buildRange(rangeKey), [rangeKey]);

  const rangeLabel = useMemo(
    () => RANGES.find((r) => r.key === rangeKey)?.label.toLowerCase(),
    [rangeKey]
  );

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getRecords(
        { from: range.from, to: range.to },
        "/dashboard/summary"
      );

      if (response?.code === 1 && response.data) {
        setData({ ...EMPTY, ...response.data });
      } else {
        setData(EMPTY);
        setError(response?.message || "Could not load the dashboard.");
      }
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      setData(EMPTY);
      setError("Could not reach the server. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const { cards, pendingBreakdown, trend, alertItems, alertSource } = data;

  return (
    <div className="page-content">
      <Container fluid>
        {/* Same page header as every other screen in the app */}
        <Breadcrumbs title="Dashboard" breadcrumbItems={BREADCRUMBS} />

        {/* ---------- period filter ---------- */}
        <Row className="mb-3">
          <Col xs={12} className="d-flex flex-wrap align-items-center gap-2">
            <span className="text-muted font-size-13 me-1">Showing</span>

            <ButtonGroup size="sm">
              {RANGES.map((item) => (
                <Button
                  key={item.key}
                  color="primary"
                  outline={rangeKey !== item.key}
                  onClick={() => setRangeKey(item.key)}
                >
                  {item.label}
                </Button>
              ))}
            </ButtonGroup>

            <Button
              size="sm"
              color="light"
              className="ms-auto"
              onClick={fetchSummary}
              disabled={loading}
              title="Refresh"
            >
              <i className={`mdi mdi-refresh ${loading ? "mdi-spin" : ""}`} />
            </Button>
          </Col>
        </Row>

        {error && (
          <div className="alert alert-danger py-2 font-size-13" role="alert">
            {error}
          </div>
        )}

        {/* ---------- headline numbers ---------- */}
        <Row className="dashboard-grid">
          <StatCard
            title="Job Incoming"
            value={cards.incoming}
            hint={`Received ${rangeLabel}`}
            icon="ri-download-2-line"
            color="primary"
            to="/item-intakes-list"
            loading={loading}
          />

          <StatCard
            title="Job Outgoing"
            value={cards.outgoing}
            hint={`Dispatched ${rangeLabel}`}
            icon="ri-upload-2-line"
            color="success"
            to="/dispatched-list"
            loading={loading}
          />

          <StatCard
            title="Pending Jobs"
            value={cards.pending}
            hint="Currently in the workshop"
            icon="ri-time-line"
            color="warning"
            to="/job-card-list"
            loading={loading}
          />

          <StatCard
            title="Alert Qty"
            value={cards.alerts}
            hint="Spare parts on low stock"
            icon="ri-alarm-warning-line"
            color="danger"
            to="/spare-part-inventory-list"
            loading={loading}
          />
        </Row>

        {/* ---------- alert qty + pending ---------- */}
        <Row className="dashboard-grid">
          <Col xl={7}>
            <AlertQtyChart
              items={alertItems}
              source={alertSource}
              loading={loading}
            />
          </Col>

          <Col xl={5}>
            <PendingJobs
              breakdown={pendingBreakdown}
              total={cards.pending}
              loading={loading}
            />
          </Col>
        </Row>

        {/* ---------- incoming vs outgoing over time ---------- */}
        <Row className="dashboard-grid">
          <Col xs={12}>
            <JobFlowChart trend={trend} loading={loading} />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Dashboard;
